// redux/api/wallet/WalletApi.js
import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "../CustomFetchBase";

export const WalletApi = createApi({
  reducerPath: "WalletApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["WALLETAPI"],
  endpoints: (build) => ({
    // Admin Endpoints
    getAllWallets: build.query({
      query: ({
        page = 1,
        role = "",
        minBalance = "",
        maxBalance = "",
        withdrawalStatus = "",
        search = "",
      }) => ({
        url: `/wallet/all?page=${page}&role=${role}&minBalance=${minBalance}&maxBalance=${maxBalance}&withdrawalStatus=${withdrawalStatus}&search=${encodeURIComponent(
          search
        )}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    getWalletByUserId: build.query({
      query: ({ userId }) => ({
        url: `/wallet/${userId}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    processWithdrawal: build.mutation({
      query: ({ transactionId, action, rejectionReason }) => ({
        url: `/wallet/process-withdrawal`,
        method: "POST",
        body: { transactionId, action, rejectionReason },
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["WALLETAPI"],
    }),

    // Driver/User Endpoints
    getMyWallet: build.query({
      query: () => ({
        url: `/wallet`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    checkWalletBalance: build.query({
      query: ({ requiredAmount }) => ({
        url: `/wallet/check-balance?requiredAmount=${requiredAmount}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    getWalletStats: build.query({
      query: ({ period = "30d" }) => ({
        url: `/wallet/stats?period=${period}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    getTransactionHistory: build.query({
      query: ({ page = 1, limit = 10, type = "", category = "" }) => ({
        url: `/wallet/transactions?page=${page}&limit=${limit}&type=${type}&category=${category}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["WALLETAPI"],
    }),

    addFunds: build.mutation({
      query: (data) => ({
        url: `/wallet/add-funds`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["WALLETAPI"],
    }),

    withdrawFunds: build.mutation({
      query: (data) => ({
        url: `/wallet/withdraw`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["WALLETAPI"],
    }),
    addAdminFunds: build.mutation({
      query: (data) => ({
        url: `/wallet/super-admin/add-funds`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["WALLETAPI"],
    }),
    decreaseAdminFunds: build.mutation({
      query: (data) => ({
        url: `/wallet/super-admin/decrease-funds`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["WALLETAPI"],
    }),
  }),
});

export const {
  // Admin hooks
  useGetAllWalletsQuery,
  useGetWalletByUserIdQuery,
  useProcessWithdrawalMutation,

  // Driver/User hooks
  useGetMyWalletQuery,
  useCheckWalletBalanceQuery,
  useGetWalletStatsQuery,
  useGetTransactionHistoryQuery,
  useAddFundsMutation,
  useWithdrawFundsMutation,
  useAddAdminFundsMutation,
  useDecreaseAdminFundsMutation,
} = WalletApi;
